import { useCallback, useMemo } from 'react';

import qs from 'qs';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfSharePageData, usePsfSharePageState } from 'src/app/pages/psf/share/pageContext';
import { usePageCopy } from 'src/app/pages/psf/share/shared/hooks';
import { useQueryParamValue } from 'src/app/shared/hooks/location';

type Result = ModelHookResult<
	{
		skipLabel: string;
		skipUrl: string;
	},
	{
		handleSkipClick: () => void;
	}
>;

export function usePageFooter(): Result {
	const allowActions = useQueryParamValue('allow_actions');
	const sourceLocation = useQueryParamValue('source_location');
	const guest = useQueryParamValue('guest');
	const fromGuestPsf = useQueryParamValue('from_guest_psf');
	const {
		data: { initial, petition },
	} = usePsfSharePageData();
	const {
		data: { skipLinkText },
	} = usePageCopy();
	const [{ shared }] = usePsfSharePageState();
	const { translate } = useI18n();
	const track = useTracking();

	const skipUrl = useMemo(() => {
		if (allowActions === 'true') {
			return `/p/${encodeURIComponent(petition.slug)}/actions?source_location=${sourceLocation || 'combo_psf'}`;
		}

		return `/p/${encodeURIComponent(petition.slug)}/d${qs.stringify(
			{
				source_location: sourceLocation || 'combo_psf',
				...(['new', 'existing'].includes(guest || '') || fromGuestPsf === '1' ? { from_guest_psf: '1' } : undefined),
			},
			{ addQueryPrefix: true },
		)}`;
	}, [allowActions, petition.slug, sourceLocation, guest, fromGuestPsf]);

	const actions = {
		handleSkipClick: useCallback(() => {
			void track('combo_psf_skip', {
				context: initial ? 'psf_combo_share_initial' : 'psf_combo_share_message',
			});
		}, [track, initial]),
	};

	if (shared) {
		return {
			data: { skipLabel: translate('corgi.psf.share.footer.continue'), skipUrl },
			actions,
		};
	}

	return {
		data: { skipLabel: skipLinkText, skipUrl },
		actions,
	};
}
